<template>
  <div class="package-price" :class="{ 'package-price_summary': variant === 'summary' }">
    <div v-if="promotion && variant === 'summary'" class="package-price__savings package-price__badge">
      Early bird · Save <Money :currency="currency" :value="promotion.discountCents" inline />
    </div>
    <div class="package-price__amounts">
      <del v-if="promotion" class="package-price__original">
        <Money class="package-price__original-amount" :currency="currency" :value="priceCents" inline />
      </del>
      <Money
        :class="{ 'package-price__discounted': promotion }"
        :currency="currency"
        :value="promotion?.priceCents ?? priceCents"
        inline
      />
    </div>
    <div v-if="promotion && variant === 'package'" class="package-price__savings">
      Early-bird savings: <Money :currency="currency" :value="promotion.discountCents" inline />
    </div>
    <slot name="after-price" />
    <span v-if="promotion" class="package-price__automatic">{{
      variant === 'summary' ? '(Coupon applied automatically)' : 'Applied automatically'
    }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Money from '@/components/Money.vue'
import { activeEarlyBirdPricing, usePromotionElapsedTime } from './pricing'
import type { EarlyBirdPricing } from './types'

const {
  currency,
  priceCents,
  earlyBirdPricing,
  variant = 'package',
} = defineProps<{
  currency: string
  priceCents: number
  earlyBirdPricing?: EarlyBirdPricing | null
  variant?: 'package' | 'summary'
}>()
const elapsedMs = usePromotionElapsedTime()
const promotion = computed(() => activeEarlyBirdPricing(earlyBirdPricing, elapsedMs.value))
</script>

<style lang="scss" scoped>
.package-price {
  display: grid;
  gap: $spacing-1;
}
.package-price__amounts {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-2;
  align-items: baseline;
}
.package-price__discounted {
  color: $brand-secondary;
}
.package-price__original {
  color: $gray;
  font-weight: $font-normal;
}
.package-price__original-amount {
  text-decoration: line-through;
}
.package-price__savings {
  color: $brand-tertiary;
  font-size: $text-sm;
  font-weight: $font-medium;
}
.package-price__badge {
  width: fit-content;
  max-width: 100%;
  padding: $spacing-1 $spacing-2;
  border-radius: $rounded-xs;
  background: rgba($brand-tertiary, 0.09);
}
.package-price__automatic {
  color: $gray;
  font-size: $text-xs;
  font-weight: $font-normal;
}
.package-price_summary {
  .package-price__discounted {
    order: -1;
    font-size: $text-2xl;
    font-weight: $font-semibold;
    line-height: $leading-snug;
  }
  .package-price__original {
    font-size: $text-sm;
  }
}
</style>
